import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import MenteePortalNav from './MenteePortalNav';
import MenteeDashboardIdentity from './MenteeDashboardIdentity';
import MenteeDashboardMentorCard from './MenteeDashboardMentorCard';
import MenteeDashboardReplyStrip from './MenteeDashboardReplyStrip';
import MenteeDashboardChecklist from './MenteeDashboardChecklist';
import MenteeDashboardUpcoming from './MenteeDashboardUpcoming';
import MenteeDashboardThreadModal from './MenteeDashboardThreadModal';
import MenteePortalService, {
    MenteeBookingRow,
    MenteeDashboardPayload,
} from './MenteePortalService';
import '../mentorDashboard.css';
import './menteeRegistration.css';
import './menteeDashboard.css';

const INELIGIBLE_COPY = 'The mentorship program is not available for your campus';

const MenteeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [payload, setPayload] = useState<MenteeDashboardPayload | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [ineligible, setIneligible] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [threadOpen, setThreadOpen] = useState(false);
    const [upcoming, setUpcoming] = useState<MenteeBookingRow[]>([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);

    const loadUpcoming = useCallback(() => {
        setUpcomingLoading(true);
        MenteePortalService.listUpcomingBookings()
            .then((data) => setUpcoming(data.bookings || []))
            .catch(() => setUpcoming([]))
            .finally(() => setUpcomingLoading(false));
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        MenteePortalService.getDashboard()
            .then((data) => {
                setPayload(data);
                setLoadError(null);
                setIneligible(false);
                setForbidden(false);
                if (data.landing === 'dashboard') {
                    loadUpcoming();
                } else {
                    setUpcoming([]);
                }
            })
            .catch((err: { status?: number; message?: string }) => {
                const status = err?.status;
                const message = MenteePortalService.errorMessage(err);
                if (status === 403 && /ineligible/i.test(message)) {
                    setIneligible(true);
                    setLoadError(null);
                    return;
                }
                if (status === 403 || status === 401) {
                    setForbidden(true);
                    setLoadError(null);
                    return;
                }
                setLoadError(message);
            })
            .finally(() => setLoading(false));
    }, [loadUpcoming]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading && !payload) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">Loading dashboard…</p>
            </div>
        );
    }

    if (ineligible) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">{INELIGIBLE_COPY}</p>
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-sub">You do not have access to Mentorship.</p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="mreg">
                <MenteePortalNav landing={null} />
                <p className="mreg-banner is-danger">{loadError}</p>
            </div>
        );
    }

    if (!payload) {
        return null;
    }

    if (payload.landing !== 'dashboard') {
        return <Navigate to="/alumni/mentorship/mentee/register" replace />;
    }

    const openThread = () => setThreadOpen(true);

    return (
        <div className="max-1250 mx-auto px-3 py-4">
            <MenteePortalNav landing={payload.landing} />
            {/* <p className="mreg-chrome">
                Mentors <span aria-hidden="true">›</span> <strong>Dashboard</strong>
            </p> */}

            <div className="card border-0 rounded-3 px-3 py-2 bg-white d-flex flex-row align-items-center justify-content-between shadow-sm mb-3">
              <div className="breadcrumb mb-0 align-items-center font-12 fw-medium text-secondary">
                Mentorship Module &nbsp;›&nbsp; <span className="text-dark fw-bold" id="bc-cur">Dashboard</span>
              </div>
              <div className="d-flex align-items-center gap-2 text-secondary font-12">
                <button className="btn btn-sm btn-light border-0" title="Help"><i className="bi bi-question-circle"></i></button>
                <button className="btn btn-sm btn-light border-0 position-relative" title="Notifications">
                  <i className="bi bi-bell"></i>
                  <span className="position-absolute top-2 start-75 translate-middle p-1 bg-danger border border-white rounded-circle"></span>
                </button>
              </div>
            </div>

            <section className="card border-0 rounded-3 text-white mb-3 shadow-sm p-4 position-relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f2155 0%, #1e3fa3 50%, #0c8a7c 100%)' }}>
                <div className="position-absolute end-0 top-0 opacity-10 translate-middle-y mt-4 me-3 display-1 pointer-events-none select-none">🎓</div>
                <div className='row align-items-center'>
                    <MenteeDashboardIdentity
                    identity={payload.identity}
                    cohortName={payload.cohort?.name ?? null}
                    cohortStart={payload.cohort?.startDate ?? null}
                    cohortEnd={payload.cohort?.endDate ?? null}
                    />
                    <MenteeDashboardMentorCard mentor={payload.mentor} />
                </div>
                
            </section>

            

            <MenteeDashboardReplyStrip
                awaitingReply={Boolean(payload.awaitingReply)}
                previewBody={payload.messagePreview?.body ?? null}
                onReply={openThread}
            />

            <MenteeDashboardChecklist
                step1={payload.checklist.step1}
                step2={payload.checklist.step2}
                step3={payload.checklist.step3}
                onOpenThread={openThread}
            />

            <div className="row g-3 mb-3">
                <div className="col-md-6">

                </div>
            </div>

            <MenteeDashboardUpcoming
                bookings={upcoming}
                menteeName={payload.identity?.name ?? null}
                isLoading={upcomingLoading}
            />

            <MenteeDashboardThreadModal
                open={threadOpen}
                onClose={() => setThreadOpen(false)}
                onSent={() => load()}
                mentorName={payload?.mentor && !payload.mentor.status ? payload.mentor.name : null}
                mentorRole={
                    payload?.mentor && !payload.mentor.status
                        ? [payload.mentor.designation, payload.mentor.company]
                              .map((part) => (part || '').trim())
                              .filter((part) => part !== '')
                              .join(' · ')
                        : null
                }
            />
        </div>
    );
};

export default MenteeDashboard;
